import { Api } from '@monorepo/shared/apiClient';
import { LogEntry } from '@monorepo/shared/componentsV2/logs/types';
import { FileData } from '@monorepo/shared/hooks/useFileInputHandler';
import { useThrottledOnArgsMessageHandler } from '@monorepo/vortex/src/contexts/pushMessages/webSocket';
import {
  EdpAiFileWithUrl,
  LogEntryResponse,
  PushMessageTopic as PMT,
  ServerToClientMessages,
} from 'mapistry-shared';
import { useCallback } from 'react';
import { MutationConfig, useMutation } from 'react-query';
import { noLogEntriesGeneratedError } from '../errorHelpers';

type Input = {
  config?: MutationConfig<void, Api.ErrorResponse>;
};

type OnLogEntriesProcessedMessage =
  ServerToClientMessages[PMT.LogEntriesGenerated];

/**
 * We want to call a specific callback when this notification comes in,
 * and the notification can only be triggered by a user interaction, and we tell the user to keep that tab open.
 * Thus not registering this listener globally, but for ease only on the required page.
 */
function useLogEntriesGeneratedMessageHandlers(
  onMessage: OnLogEntriesProcessedMessage,
) {
  useThrottledOnArgsMessageHandler(PMT.LogEntriesGenerated, (props) =>
    onMessage({
      logEntries: props.logEntries,
      files: props.files,
      error: props.error,
      tabIdentifier: props.tabIdentifier,
    }),
  );
}

function useStartGeneratingLogEntries(input?: Input) {
  return useMutation(Api.generateLogEntries, input?.config);
}

export function useGenerateLogEntriesAndListenForPushMessageResponse({
  organizationId,
  projectId,
  logId,
  fileIsReady,
  fileErrored,
}: {
  organizationId: string;
  projectId: string;
  logId: string;
  fileIsReady: (
    requestId: string,
    logEntries?: LogEntry[],
    processedFile?: EdpAiFileWithUrl,
  ) => void;
  fileErrored: (
    requestId: string,
    error?: Api.ErrorResponse,
    processedFile?: EdpAiFileWithUrl,
  ) => void;
}) {
  const [startGeneratingLogEntries] = useStartGeneratingLogEntries();

  const handleUploadLogEntriesImage = useCallback(
    (filesToUpload: FileData[]) => {
      if (!filesToUpload?.length) return;

      filesToUpload.forEach(({ file, requestId }) => {
        void startGeneratingLogEntries(
          {
            organizationId,
            logId,
            projectId,
            file,
            tabIdentifier: requestId,
          },
          {
            onError: (e) => {
              fileErrored(requestId, e);
            },
          },
        );
      });
    },
    [logId, organizationId, projectId, startGeneratingLogEntries, fileErrored],
  );

  const onFinishedGeneratingLogEntries = useCallback(
    ({
      logEntries: generatedLogEntries,
      files,
      error: serverErrorMessage,
      tabIdentifier: requestId,
    }: {
      logEntries?: LogEntryResponse[];
      files?: EdpAiFileWithUrl[];
      error?: string;
      tabIdentifier: string;
    }) => {
      // Since we are only processing one file at a time (but multiple files can be uploaded in parallel)
      // we can assume the first file is the one we're tracking
      const processedFile = files?.[0];

      if (generatedLogEntries?.length) {
        fileIsReady(requestId, generatedLogEntries, processedFile);
        return;
      }

      const errorMessage = serverErrorMessage || noLogEntriesGeneratedError;
      const errorResponse = { data: errorMessage } as Api.ErrorResponse;

      fileErrored(requestId, errorResponse, processedFile);
    },
    [fileErrored, fileIsReady],
  );

  // Start listening to push notifications for log entries generation
  useLogEntriesGeneratedMessageHandlers(onFinishedGeneratingLogEntries);

  return {
    handleUploadLogEntriesImage,
  };
}
